// ── Purchase discounts (invoices/{id}/adjustments, kind 'discount') ─────────
// A discount the supplier gives on a purchase lowers what we owe (the
// invoice's creditedUSD, same lever as a purchase return) and is booked as
// Purchase Discounts Received income (Dr Payable / Cr purchase-discounts).
// Listed in the purchase popup on every page; Apply / Edit / Delete only
// where the popup is given `canEdit`. Each change moves the invoice, its
// due date group (separate mode), the journal entry and the Activity Log
// in one transaction.

import { useEffect, useState } from 'react';
import { collection, doc, onSnapshot, runTransaction, type Transaction, type Firestore } from 'firebase/firestore';
import { getClientDb } from '../firebase/clientApp';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../hooks/useSettings';
import { logActivity } from '../utils/activity';
import { buildEntry, postEntry, entryId, linesPurchaseDiscount } from '../utils/ledger';
import { dueGroupOutstanding, dueGroupStatus } from '../utils/dueGroups';
import { fmtMoney } from '../utils/currency';
import { localISO } from '../utils/localDate';
import { Button, Modal, ConfirmModal, FormField, DeleteIconButton } from './ui';
import type { PurchaseInvoice, InvoiceAdjustment, PaymentStatus, DueGroup } from '../types';

const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;
type Discount = InvoiceAdjustment & { dueGroupId?: string | null };

const statusOf = (paid: number, credited: number, total: number): PaymentStatus =>
  paid + credited >= total - 0.005 ? 'paid' : paid + credited > 0.005 ? 'partial' : 'unpaid';

const owedOn = (inv: PurchaseInvoice) =>
  Math.max(0, inv.totalAmountUSD - (inv.paidUSD ?? 0) - (inv.creditedUSD ?? 0));

const fmtDate = (d: string) => d
  ? new Date(d + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
  : '—';

/** Moves `delta` (negative = less discount) onto the invoice and, in separate
 *  mode, onto the due date group it sits on. Reads the invoice first. */
async function moveDiscount(tx: Transaction, db: Firestore, invoiceId: string, delta: number, groupId: string | null | undefined) {
  const invRef = doc(db, 'invoices', invoiceId);
  const snap = await tx.get(invRef);
  if (!snap.exists()) throw new Error('That invoice no longer exists.');
  const inv = { id: invoiceId, ...snap.data() } as PurchaseInvoice;
  const groups: DueGroup[] = inv.dueGroups ?? [];
  const gi = groupId ? groups.findIndex(g => g.id === groupId) : -1;
  if (delta > 0.005) {
    const room = gi >= 0 ? dueGroupOutstanding(groups[gi]) : owedOn(inv);
    if (delta > room + 0.005) throw new Error(`Only ${round2(room)} is still owed, the discount can't be more than that.`);
  }
  const credited = round2(Math.max(0, (inv.creditedUSD ?? 0) + delta));
  const discount = round2(Math.max(0, (inv.discountUSD ?? 0) + delta));
  const newGroups = gi < 0 ? null : groups.map((g, i) => {
    if (i !== gi) return g;
    const moved = { ...g, creditedUSD: round2(Math.max(0, g.creditedUSD + delta)) };
    return { ...moved, paymentStatus: dueGroupStatus(moved) };
  });
  return {
    inv,
    apply: () => tx.update(invRef, {
      creditedUSD: credited, discountUSD: discount,
      paymentStatus: statusOf(inv.paidUSD ?? 0, credited, inv.totalAmountUSD),
      ...(newGroups ? { dueGroups: newGroups } : {}),
      updatedAt: new Date().toISOString(),
    }),
  };
}

// ── Apply / Edit form ────────────────────────────────────────────────────────
function DiscountFormModal({ invoice, editing, onClose }: {
  invoice: PurchaseInvoice; editing: Discount | null; onClose: () => void;
}) {
  const { user, business } = useAuth();
  const settings = useSettings();
  const money = (v: number) => fmtMoney(v, settings.currency);
  const [amount, setAmount] = useState(editing ? String(editing.amountUSD) : '');
  const [date, setDate]     = useState(editing?.date ?? localISO(new Date()));
  const [reason, setReason] = useState(editing?.reason ?? '');
  const [notes, setNotes]   = useState(editing?.notes ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  // What this discount can grow to: what's still owed plus what it already takes off.
  const maxAmt = round2(owedOn(invoice) + (editing?.amountUSD ?? 0));

  async function save() {
    const amt = round2(parseFloat(amount));
    if (isNaN(amt) || amt <= 0) return setError('Enter a discount amount.');
    if (amt > maxAmt + 0.005) return setError(`The discount can't be more than ${money(maxAmt)}.`);
    if (!date) return setError('Pick a date.');
    if (!reason.trim()) return setError('Enter a reason.');
    const db = getClientDb();
    if (!db || !business) return setError('Not connected.');
    setSaving(true); setError('');
    try {
      await runTransaction(db, async tx => {
        let adjRef, groupId: string | null;
        if (editing) {
          adjRef = doc(db, 'invoices', invoice.id, 'adjustments', editing.id);
          groupId = editing.dueGroupId ?? null;
        } else {
          adjRef = doc(collection(db, 'invoices', invoice.id, 'adjustments'));
          // Several due dates: the discount goes on the first one still open.
          const open = invoice.dueDateMode === 'separate'
            ? (invoice.dueGroups ?? []).find(g => dueGroupOutstanding(g) > 0.005) : undefined;
          groupId = open?.id ?? null;
        }
        const delta = round2(amt - (editing?.amountUSD ?? 0));
        const { apply } = await moveDiscount(tx, db, invoice.id, delta, groupId);
        apply();
        if (editing) {
          tx.update(adjRef, { amountUSD: amt, date, reason: reason.trim(), notes: notes.trim() || null, updatedAt: new Date().toISOString(), updatedBy: user?.id ?? '' });
        } else {
          tx.set(adjRef, {
            kind: 'discount', date, amountUSD: amt, dueGroupId: groupId,
            reason: reason.trim(), notes: notes.trim() || null,
            createdAt: new Date().toISOString(), createdBy: user?.id ?? '',
          });
        }
        postEntry(tx, db, buildEntry({
          businessId: business.id, sourceType: 'purchase_discount', sourceId: adjRef.id,
          sourceCollection: `invoices/${invoice.id}/adjustments`, date, currency: settings.currency,
          createdBy: user?.id ?? '', note: `Discount on ${invoice.invoiceNo}: ${reason.trim()}`,
          lines: linesPurchaseDiscount(amt, invoice.fromEntityId),
        }), entryId('purchase_discount', adjRef.id));
        logActivity(tx, db, business.id, user, {
          module: 'payment', action: editing ? 'edit' : 'create', recordId: invoice.id, recordNo: invoice.invoiceNo,
          partyId: invoice.fromEntityId, amountUSD: amt, prevAmountUSD: editing?.amountUSD,
          note: `Discount: ${reason.trim()}`,
        });
      });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save the discount.');
    }
    setSaving(false);
  }

  return (
    <Modal open onClose={onClose} title={editing ? 'Edit Discount' : 'Apply Discount'} size="sm"
      footer={<>
        <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="primary" onClick={save} loading={saving}>{editing ? 'Save Changes' : 'Apply'}</Button>
      </>}>
      {error && <div className="msg msg-error" style={{ marginBottom: 12 }}>{error}</div>}
      <div style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 12 }}>
        {invoice.invoiceNo}: up to <strong>{money(maxAmt)}</strong>. A discount lowers what you owe and shows as income in Profit &amp; Loss.
      </div>
      <div className="form-row-2">
        <FormField label={`Amount (${settings.currency})`} required>
          <input type="number" step="0.01" min="0" className="no-spinner" value={amount}
            onChange={e => setAmount(e.target.value)} placeholder="0.00" autoFocus />
        </FormField>
        <FormField label="Date" required>
          <input type="date" value={date} max={localISO(new Date())} onChange={e => setDate(e.target.value)} />
        </FormField>
      </div>
      <FormField label="Reason" required>
        <input value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. early payment, price correction" />
      </FormField>
      <FormField label="Notes (optional)">
        <input value={notes} onChange={e => setNotes(e.target.value)} />
      </FormField>
    </Modal>
  );
}

// ── Section in the purchase popup ───────────────────────────────────────────
export function PurchaseDiscountsSection({ invoice, canEdit }: { invoice: PurchaseInvoice; canEdit: boolean }) {
  const { user, business } = useAuth();
  const settings = useSettings();
  const money = (v: number) => fmtMoney(v, settings.currency);
  const [list, setList]         = useState<Discount[]>([]);
  const [formFor, setFormFor]   = useState<Discount | 'new' | null>(null);
  const [deleting, setDeleting] = useState<Discount | null>(null);
  const [busy, setBusy]         = useState(false);
  const [error, setError]       = useState('');

  useEffect(() => {
    const db = getClientDb();
    if (!db) return;
    return onSnapshot(collection(db, 'invoices', invoice.id, 'adjustments'), snap => {
      setList(snap.docs.map(d => ({ id: d.id, ...d.data() } as Discount))
        .filter(d => d.kind === 'discount')
        .sort((a, b) => b.date.localeCompare(a.date) || (b.createdAt ?? '').localeCompare(a.createdAt ?? '')));
    }, () => setList([]));
  }, [invoice.id]);

  async function confirmDelete() {
    if (!deleting || !business) return;
    const db = getClientDb();
    if (!db) return setError('Not connected.');
    setBusy(true); setError('');
    try {
      await runTransaction(db, async tx => {
        const { apply } = await moveDiscount(tx, db, invoice.id, -deleting.amountUSD, deleting.dueGroupId);
        apply();
        tx.delete(doc(db, 'invoices', invoice.id, 'adjustments', deleting.id));
        tx.delete(doc(db, 'journalEntries', entryId('purchase_discount', deleting.id)));
        logActivity(tx, db, business.id, user, {
          module: 'payment', action: 'delete', recordId: invoice.id, recordNo: invoice.invoiceNo,
          partyId: invoice.fromEntityId, amountUSD: deleting.amountUSD, note: `Discount: ${deleting.reason}`,
        });
      });
      setDeleting(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not delete the discount.');
    }
    setBusy(false);
  }

  const owed = owedOn(invoice);
  if (list.length === 0 && !(canEdit && owed > 0.005)) return null;
  const total = list.reduce((s, d) => s + (d.amountUSD ?? 0), 0);

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <div className="form-section-label" style={{ margin: 0 }}>Discounts</div>
        {canEdit && owed > 0.005 && (
          <Button variant="secondary" size="sm" onClick={() => setFormFor('new')}>Apply Discount</Button>
        )}
      </div>
      {list.length === 0 ? (
        <div style={{ fontSize: 12, color: 'var(--text-3)' }}>No discounts on this invoice.</div>
      ) : (
        <div className="table-wrap">
          <table style={{ minWidth: 0 }}>
            <thead>
              <tr>
                <th>Date</th><th>Reason</th><th>Notes</th>
                <th style={{ textAlign: 'right' }}>Amount</th>
                {canEdit && <th style={{ width: 90 }} />}
              </tr>
            </thead>
            <tbody>
              {list.map(d => (
                <tr key={d.id}>
                  <td style={{ fontFamily: 'var(--font-num)', whiteSpace: 'nowrap' }}>{fmtDate(d.date)}</td>
                  <td>{d.reason || <span style={{ opacity: 0.4 }}>—</span>}</td>
                  <td>{d.notes || <span style={{ opacity: 0.4 }}>—</span>}</td>
                  <td style={{ textAlign: 'right', fontFamily: 'var(--font-num)' }}>{money(d.amountUSD ?? 0)}</td>
                  {canEdit && (
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <div className="action-row" style={{ justifyContent: 'flex-end' }}>
                        <Button variant="secondary" size="sm" onClick={() => setFormFor(d)}>Edit</Button>
                        <DeleteIconButton onClick={() => { setDeleting(d); setError(''); }} />
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
            {list.length > 1 && (
              <tfoot>
                <tr>
                  <td colSpan={3} style={{ fontWeight: 600 }}>Total · {list.length} discounts</td>
                  <td style={{ textAlign: 'right', fontFamily: 'var(--font-num)', fontWeight: 600 }}>{money(total)}</td>
                  {canEdit && <td />}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}

      {formFor && (
        <DiscountFormModal invoice={invoice} editing={formFor === 'new' ? null : formFor} onClose={() => setFormFor(null)} />
      )}
      <ConfirmModal
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={confirmDelete}
        title="Delete Discount"
        message={deleting ? `Remove the ${money(deleting.amountUSD)} discount on ${invoice.invoiceNo}? The amount goes back to what you owe and comes off Purchase Discounts Received.` : ''}
        confirmLabel="Delete"
        loading={busy}
        error={error}
      />
    </div>
  );
}
